import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TimeZoneReportParameter } from '@ndt/report-output/shared/models/time-zone-report-parameters';
import {
   convertFromFormValueList,
   ParameterFormValueUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-form-value';
import {
   BooleanReportParameter,
   convertToForm,
   ReportParametersUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import { ReportParameterConfig } from '@ndt/reports/shared/report-parameter-list/report-parameter.config';
import {
   ReportParameterFormService,
   ReportParameterFormServiceBase,
} from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form.service';
import { ReportParameters } from 'generated/graphql-types';
import { LcsDateTimeAdapter } from 'src/app/common-components/date-time-picker/configuration/date-time-adapter';
import { TimeZoneOption } from 'src/app/common-components/entity-select/timezone/models/time-zone-option';
import { ClockReportParameters } from '../models/clock-report-parameters';

export class ClockParametersFormService
   extends ReportParameterFormServiceBase
   implements ReportParameterFormService<ClockReportParameters>
{
   config: ReportParameterConfig[];

   form: UntypedFormGroup;

   constructor(fb: UntypedFormBuilder, private dateAdapter: LcsDateTimeAdapter<Date>) {
      super(fb);

      const initialValue: ReportParametersUnion[] = [
         {
            parameterType: ReportParameters.ShowDate,
            sortOrder: 1,
            value: false,
            isAskParameter: false,
         },
         {
            parameterType: ReportParameters.TimeZone,
            sortOrder: 2,
            value: '',
         },
      ];

      this.config = [
         {
            label: 'Show Date',
            type: 'bool',
         },
         {
            label: 'Time Zone',
            type: 'timeZone',
         },
      ];

      this.form = convertToForm(initialValue, fb);
   }

   convertValue(values: ReportParametersUnion[]): ClockReportParameters | null {
      const showDate = values.find((v) => {
         return v.parameterType === ReportParameters.ShowDate;
      }) as BooleanReportParameter;

      const timeZone = values.find((v) => {
         return v.parameterType === ReportParameters.TimeZone;
      }) as TimeZoneReportParameter | undefined;

      const output: ClockReportParameters = {
         showDate,
         timeZone,
         tileLayoutConfiguration: this.getLayoutConfigurations(values),
      };

      return output;
   }

   getValue(): ClockReportParameters | null {
      const formValue = this.form.getRawValue();

      const values: ParameterFormValueUnion[] = this.convertTimeZoneValue(formValue.values);
      const convertedValues = convertFromFormValueList(values);
      return this.convertValue(convertedValues);
   }

   isListComplete(values: ReportParametersUnion[], _includeAskParameter = false): boolean {
      const showDate = values.find((v) => {
         return v.parameterType === ReportParameters.ShowDate;
      });
      const timeZone = values.find((v) => {
         return v.parameterType === ReportParameters.TimeZone;
      });

      return !!showDate && !!timeZone;
   }

   orderValues(): void {
      const { values } = this.form.value;

      this.form.setValue({
         values: [
            ...values
               .filter((v) => v.hasOwnProperty('sortOrder'))
               .map((v) => {
                  return {
                     ...v,
                     sortOrder:
                        v.parameterType === ReportParameters.ShowDate
                           ? 1
                           : v.parameterType === ReportParameters.TimeZone
                           ? 2
                           : v.sortOrder != null
                           ? v.sortOrder
                           : 2,
                  };
               }),
         ],
      });
   }

   patchSavedValues(values: ReportParametersUnion[]): void {
      super.patchSavedValues(values, false, false);
   }

   setValue(values: ParameterFormValueUnion[]): void {
      this.form.setValue(values);
   }

   private convertTimeZoneValue(values): ParameterFormValueUnion[] {
      return values.map((v) => {
         if (v.parameterType === ReportParameters.TimeZone) {
            return { ...v, value: (v.value as TimeZoneOption)?.timeZoneId ?? '' };
         }
         return v;
      });
   }
}
